import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./Schedules.css";
import AssignScreenModal from "./AssignScreenModal";

const Schedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch schedules from Firestore
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const schedulesRef = collection(db, "schedules");
        const querySnapshot = await getDocs(schedulesRef);
        const schedulesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSchedules(schedulesData);
      } catch (err) {
        console.error("❌ Error fetching schedules:", err.message);
      }
    };

    fetchSchedules();
  }, []);

  const getStatus = (schedule) => {
    const now = new Date();
    const start = new Date(schedule.startTime);
    const end = new Date(schedule.endTime);

    if (end < now) return "expired";
    if (start > now) return "upcoming";
    return "active";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("❗ Are you sure you want to delete this schedule?")) return;
    try {
      await deleteDoc(doc(db, "schedules", id));
      setSchedules(schedules.filter((schedule) => schedule.id !== id));
      alert("✅ Schedule deleted successfully!");
    } catch (err) {
      console.error("❌ Error deleting schedule:", err.message);
      alert("⚠️ Failed to delete schedule.");
    }
  };

  const handleAssignScreen = (schedule) => {
    setSelectedSchedule(schedule);
    setIsAssignModalOpen(true);
  };

  const handleScreenAssigned = (screen) => {
    const updatedSchedules = schedules.map((schedule) =>
      schedule.id === selectedSchedule.id ? { ...schedule, screenId: screen.id, screenName: screen.name } : schedule
    );
    setSchedules(updatedSchedules);
    setIsAssignModalOpen(false);
  };

  const handleFilterChange = (filterValue) => {
    setFilter(filterValue);
    setIsDropdownOpen(false);
  };

  const filteredSchedules = schedules
    .filter((schedule) =>
      schedule.contentName?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((schedule) => {
      const status = getStatus(schedule);
      if (filter === "all") return true;
      return status === filter;
    });

  return (
    <div className="schedules-container">
      <div className="schedules-header">
        <h1 className="schedules-title">Schedules</h1>
        <button className="btn add-schedule" onClick={() => navigate("/new-schedule")}>
          Add Schedule
        </button>
      </div>

      <div className="search-bar-container">
        <input
          type="text"
          className="search-bar"
          placeholder="Search schedules..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="filter-container">
        <div className="dropdown">
          <button className="dropdown-toggle" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            Filter: {filter.charAt(0).toUpperCase() + filter.slice(1)} ▼
          </button>
          {isDropdownOpen && (
            <div className="dropdown-menu">
              <div onClick={() => handleFilterChange("all")}>All</div>
              <div onClick={() => handleFilterChange("upcoming")}>Upcoming</div>
              <div onClick={() => handleFilterChange("active")}>Active</div>
              <div onClick={() => handleFilterChange("expired")}>Expired</div>
            </div>
          )}
        </div>
      </div>

      <div className="schedules-table-container">
        <table className="schedules-table">
          <thead>
            <tr>
              <th>Schedule Name</th>
              <th>Date Created</th>
              <th>Status</th>
              <th>Assigned Screen</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSchedules.length > 0 ? (
              filteredSchedules.map((schedule) => (
                <tr key={schedule.id}>
                  <td>{schedule.contentName}</td>
                  <td>{new Date(schedule.dateCreated).toLocaleDateString()}</td>
                  <td className={`status-${getStatus(schedule)}`}>
                    {getStatus(schedule).charAt(0).toUpperCase() + getStatus(schedule).slice(1)}
                  </td>
                  <td>{schedule.screenName || "Not Assigned"}</td>
                  <td>
                    <button className="btn edit" onClick={() => navigate(`/edit-schedule/${schedule.id}`)}>✏️ Edit</button>
                    <button className="btn assign" onClick={() => handleAssignScreen(schedule)}>📺 Assign to Screen</button>
                    <button className="btn delete" onClick={() => handleDelete(schedule.id)}>❌ Delete</button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">No schedules found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AssignScreenModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onScreenAssigned={handleScreenAssigned}
      />
    </div>
  );
};

export default Schedules;
